export function mapQuickBudgetToTemplate({
  selectedClient,
  projectName,
  ambientes,
  items,
  consumables,
  diferenciais,
  inclusoes,
  naoInclusoes,
  notes,
  validityDays,
  pricingCalculation,
  consultantName,
  consultantPhone,
  complexityMultiplier,
  totalPreferenceWeight,
  marginPercent
}) {
  const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }).format(value || 0);

  const formatCurrency0 = (value) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(value || 0);

  const getItemTotal = (item) => {
    if (item.source === 'pdf') {
      return (parseFloat(item.quantity) || 1) * (parseFloat(item.unit_price) || 0);
    }
    return parseFloat(item.unit_price) || 0;
  };

  const today = new Date();
  const validityDate = new Date(today);
  validityDate.setDate(validityDate.getDate() + (validityDays || 30));

  const pad = (n) => String(n).padStart(2, '0');
  const fmtDate = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

  const products = ambientes.map((amb) => {
    const ambItems = amb.items || [];
    const specifications = ambItems.map((item) => {
      const qty = parseFloat(item.quantity) || 1;
      const unitPrice = parseFloat(item.unit_price) || 0;
      const total = qty * unitPrice;
      const dims = item.largura_cm && item.comprimento_cm
        ? ` ${item.largura_cm}×${item.comprimento_cm}cm`
        : '';
      return `${qty}× ${item.item_name || 'Peça'}${dims} — ${formatCurrency(total)}`;
    });

    const ambConsumables = amb.consumables || [];
    const consSpecs = ambConsumables.map((c) => {
      const qty = parseFloat(c.quantity) || 1;
      const unitPrice = parseFloat(c.unit_price) || 0;
      return `${qty}× ${c.name} — ${formatCurrency(qty * unitPrice)}`;
    });

    const allSpecs = [...specifications, ...consSpecs];
    const ambTotal = ambItems.reduce((s, i) => s + getItemTotal(i), 0);
    const ambConsTotal = ambConsumables.reduce((s, c) => s + (parseFloat(c.quantity) || 1) * (parseFloat(c.unit_price) || 0), 0);

    const categoryName = (amb.nome || 'AMBIENTE').split(' ')[0].toUpperCase();

    return {
      id: amb.id || `amb-${ambientes.indexOf(amb)}`,
      name: amb.nome || `Ambiente ${ambientes.indexOf(amb) + 1}`,
      category: categoryName,
      description: amb.descricao || `${ambItems.length} peça(s) planejada(s)`,
      specifications: allSpecs,
      price: Math.round((ambTotal + ambConsTotal) * 100) / 100,
      image: null
    };
  });

  const allSpecLines = [];
  if (items && items.length > 0) {
    items.forEach((item) => {
      const qty = parseFloat(item.quantity) || 1;
      const unitPrice = parseFloat(item.unit_price) || 0;
      const dims = item.largura_cm && item.comprimento_cm
        ? ` ${item.largura_cm}×${item.comprimento_cm}cm`
        : item.width_cm && item.height_cm
        ? ` ${item.width_cm}×${item.height_cm}cm`
        : '';
      allSpecLines.push(`${qty}× ${item.item_name || item.name || 'Peça'}${dims} — ${formatCurrency(qty * unitPrice)}`);
    });
  }
  if (allSpecLines.length > 0) {
    products.push({
      id: 'global-items',
      name: 'Peças Avulsas',
      category: 'AVULSO',
      description: `${items.length} peça(s)`,
      specifications: allSpecLines,
      price: Math.round(items.reduce((s, i) => s + getItemTotal(i), 0) * 100) / 100,
      image: null
    });
  }

  const genPaymentLabel = () => {
    if (!pricingCalculation) return '';
    return pricingCalculation.paymentLabel || '';
  };

  const pc = pricingCalculation || {};
  const finalPrice = pc.finalPrice || 0;
  const parcelValue = pc.parcelValue || finalPrice;
  const installmentCount = pc.installmentCount || 1;

  const payment = {
    cash: pc.downPaymentHasDiscount && pc.downPaymentDiscountPercent > 0
      ? `${pc.downPaymentDiscountPercent}% de desconto à vista no PIX ou Dinheiro`
      : 'Desconto especial à vista — consulte',
    card: installmentCount > 1
      ? `Em até ${installmentCount}x sem juros no cartão`
      : 'Em até 12x sem juros no cartão',
    boleto: pc.hasDownPayment && pc.downPaymentPercent > 0
      ? `Entrada ${pc.downPaymentPercent}% + parcelas via boleto`
      : 'Entrada + parcelas via boleto',
    installments: installmentCount > 1
      ? `${installmentCount}x de ${formatCurrency0(parcelValue)} sem juros`
      : `${formatCurrency0(finalPrice)} à vista`
  };

  const subtotal = pc.basePrice || 0;
  const discountAmount = pc.discountAmount || 0;

  const totals = {
    subtotal: Math.round(subtotal * 100) / 100,
    excludedValue: 0,
    discount: Math.round(discountAmount * 100) / 100,
    shipping: 0,
    assembly: 0,
    total: Math.round(finalPrice * 100) / 100
  };

  const advantages = diferenciais && diferenciais.length > 0
    ? diferenciais
    : ['Qualidade premium', 'Atendimento personalizado'];

  const excludedItems = naoInclusoes && naoInclusoes.length > 0
    ? naoInclusoes
    : ['Elétrica', 'Gesso', 'Pintura'];

  const observations = notes && notes.trim()
    ? notes.split('\n').filter((l) => l.trim())
    : ['Prazo de entrega: 30 dias úteis após aprovação do projeto'];

  if (consultantName) {
    observations.push(`Consultor: ${consultantName}${consultantPhone ? ` — ${consultantPhone}` : ''}`);
  }

  return {
    company: {
      name: 'Santiago Móveis Planejados',
      tagline: 'MÓVEIS PLANEJADOS',
      logo: null
    },
    customer: {
      name: selectedClient?.full_name || selectedClient?.name || '-',
      phone: selectedClient?.phone || '-',
      email: selectedClient?.email || '-'
    },
    project: {
      date: fmtDate(today),
      validity: fmtDate(validityDate),
      number: 'PREVIA'
    },
    products,
    payment,
    totals,
    advantages,
    excludedItems,
    observations
  };
}
